(() => {
  const DRAWER_NAME = "cart-drawer";
  const FALLBACK_MOTION_MS = 250;

  class CartDrawer extends HTMLElement {
    #abortController = null;
    #dialog = null;
    #refreshTail = Promise.resolve();
    #closeTimer = 0;
    #closeListener = null;
    #closing = false;
    #pendingThresholds = [];

    connectedCallback() {
      this.#dialog = this.querySelector("[data-cart-drawer-dialog]");
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      document.addEventListener("click", this.#handleDocumentClick, { signal });
      document.addEventListener("submit", this.#handleDocumentSubmit, {
        signal,
        capture: true,
      });
      this.#dialog?.addEventListener("click", this.#handleDialogClick, {
        signal,
      });
      this.#dialog?.addEventListener("close", this.#handleDialogClose, {
        signal,
      });
      this.#dialog?.addEventListener("cancel", this.#handleDialogCancel, {
        signal,
      });

      // The gift controller asks its host surface to re-render after it has
      // added or removed a reward line. Claiming the event keeps it from
      // falling back to a full page reload, which would shut the drawer.
      document.addEventListener("fgwp:refresh", this.#handleGiftRefresh, {
        signal,
      });

      // On /cart both surfaces render the same bag. A change made on the page
      // has to reach this drawer too, or it shows lines that are gone.
      document.addEventListener("cart:updated", this.#handleExternalUpdate, {
        signal,
      });

      this.#bindInteractions();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = null;
      this.#clearCloseTimer();
    }

    /**
     * Slide in from the right with the scrim fading up. showModal() puts the
     * dialog in the top layer with the panel at its resting, off-screen
     * position; one forced layout later the `is-open` class moves it in and
     * the CSS transition carries it. That class is the whole mechanism.
     */
    open() {
      if (!this.#dialog || this.#dialog.open) return;

      this.#cancelClose();
      this.#dialog.showModal();

      // Layout has to see the off-screen position once before the class
      // changes it, or there is nothing for the transition to start from.
      this.querySelector(".cart-drawer__panel")?.getBoundingClientRect();
      this.#dialog.classList.add("is-open");
      this.#playThresholds();
    }

    // For code that adds to the cart outside a product form — the Kitenzo
    // bundle builder's after-add hook (assets/kitenzo-bridge.js) — and needs
    // the drawer to show the new contents. Same shape as the form-add path
    // below, minus the submit button's busy state.
    //
    // The section refresh runs against `window.location.pathname`, which on a
    // bundle page is Kitenzo's app-proxy path. Shopify still answers the
    // Section Rendering API there: `/apps/bundles/bundle/152523?sections=<id>`
    // returns the same 11KB of drawer JSON as `/?sections=<id>`, verified on
    // the storefront. The `#fetchCart()` branch below is the same defensive
    // fallback the form-add path carries, not the usual route.
    //
    // Both options are for a caller that already has what it needs (the
    // Perfect Hue drawer, assets/product-upsell-drawer.js); the Kitenzo path
    // passes neither and behaves as before.
    //
    // `sections` — a bundled section rendering map from /cart/add.js. When it
    // carries this drawer, that copy goes in instead of a second request; if
    // it cannot be used, the drawer fetches as usual.
    //
    // `openAfter` — a promise to wait for between refreshing and opening, so
    // the content is refreshed while another panel is still leaving and this
    // one slides in the moment it has gone, not a round trip later. The open
    // then runs in a task of its own, as it does from the bag icon's click.
    // Whatever settles openAfter can be running inside a rendering update —
    // the Perfect Hue drawer's is a transitionend — and a slide started from
    // there was timed from that frame in WebKit: in an iPhone profile the
    // panel went from off-screen to in place with nothing in between (PO
    // review, 23 Sep, "slides in quicker than from the bag icon").
    async refreshAndOpen({ sections, openAfter } = {}) {
      const rendered = sections?.[this.dataset.sectionId];
      const refreshed = rendered
        ? await this.#refresh(async () => {
            // Guarded like the fetch loop's own apply: a throw here would
            // otherwise mean the drawer never opens at all.
            try {
              if (this.#applySectionHtml(rendered)) return true;
            } catch {
              // Fall through to fetching.
            }
            return this.#refreshSectionContent();
          })
        : await this.#refresh();

      if (!refreshed) {
        const cart = await this.#fetchCart();

        if (cart) {
          this.#syncItemCount(cart.item_count);
        }

        this.#showError(this.dataset.errorRefresh ?? "");
      }

      // Waited for whatever happened above, so a failed refresh does not open
      // this drawer over a panel that is still on its way out.
      if (openAfter) {
        await openAfter;
        await new Promise((resolve) => window.setTimeout(resolve, 0));
      }

      this.#announceCartUpdate();
      this.open();
    }

    /**
     * Runs the callback once the current styles have been painted — two frames
     * on, or after 100ms when frames are not being served (a hidden tab pauses
     * requestAnimationFrame).
     */
    #afterPaint(callback) {
      let done = false;
      const run = () => {
        if (done) return;

        done = true;
        callback();
      };

      requestAnimationFrame(() => requestAnimationFrame(run));
      window.setTimeout(run, 100);
    }

    /**
     * Drops the class so the panel slides out and the scrim fades, then closes
     * the dialog once the panel's transition has ended — dialog.close() flips
     * display straight away, so it has to wait for the motion.
     */
    close() {
      if (!this.#dialog?.open || this.#closing) return;

      const dialog = this.#dialog;
      const panel = this.querySelector(".cart-drawer__panel");

      const finish = () => {
        this.#cancelClose();
        if (dialog.open) dialog.close();
      };

      this.#closing = true;
      dialog.classList.remove("is-open");

      if (this.#prefersReducedMotion() || !panel) {
        finish();
        return;
      }

      this.#closeListener = (event) => {
        if (event.target === panel && event.propertyName === "transform") {
          finish();
        }
      };
      panel.addEventListener("transitionend", this.#closeListener);

      // A transitionend can go missing (tab in the background, transition cut
      // short by a style change); the timer makes sure the drawer still shuts.
      this.#closeTimer = window.setTimeout(finish, this.#motionMs() + 50);
    }

    #cancelClose() {
      this.#closing = false;

      if (this.#closeTimer) {
        window.clearTimeout(this.#closeTimer);
        this.#closeTimer = 0;
      }

      if (this.#closeListener) {
        this.querySelector(".cart-drawer__panel")?.removeEventListener(
          "transitionend",
          this.#closeListener,
        );
        this.#closeListener = null;
      }
    }

    #clearCloseTimer() {
      this.#cancelClose();
    }

    #prefersReducedMotion() {
      return (
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
      );
    }

    // Reads the header's motion token so the JS timer and the CSS transition
    // cannot drift apart when the token changes.
    #motionMs() {
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue("--search-motion-duration")
        .trim();
      const value = Number.parseFloat(raw);

      if (!Number.isFinite(value) || value <= 0) return FALLBACK_MOTION_MS;

      return raw.endsWith("ms") ? value : value * 1000;
    }

    #handleDocumentClick = (event) => {
      const openTrigger = event.target.closest("[data-cart-drawer-open]");
      if (openTrigger) {
        event.preventDefault();
        this.open();
        return;
      }

      const closeTrigger = event.target.closest("[data-cart-drawer-close]");
      if (closeTrigger && this.contains(closeTrigger)) {
        event.preventDefault();
        this.close();
        return;
      }

      const upsellAdd = event.target.closest("[data-cart-upsell-add]");
      if (upsellAdd && this.contains(upsellAdd)) {
        event.preventDefault();
        this.#addUpsell(upsellAdd);
      }
    };

    /**
     * The recommendations carousel is the cart page's component, rendered here
     * too. Size selection is shared in assets/cart-upsell.js; adding to the bag
     * is per-host because each one re-renders itself differently.
     */
    async #addUpsell(button) {
      if (button.disabled) return;

      const card = button.closest("[data-cart-upsell-card]");
      const variantId = Number(button.dataset.variantId);
      if (!variantId) return;

      this.#clearError();
      button.disabled = true;
      card?.setAttribute("aria-busy", "true");

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });

        if (!response.ok) {
          throw new Error(await this.#getCartErrorMessage(response, ""));
        }

        await response.json();
        await this.#refresh();
        button.closest("cart-upsell")?.dropCard?.(button);
        this.#announceCartUpdate();
      } catch {
        card?.removeAttribute("aria-busy");
        button.disabled = false;
        this.#showError(this.dataset.errorAdd ?? "");
      }
    }

    #handleDialogClick = (event) => {
      if (event.target === this.#dialog) {
        this.close();
      }
    };

    #handleDialogClose = () => {
      this.#dialog?.classList.remove("is-open");
      this.#cancelClose();
    };

    #handleDialogCancel = (event) => {
      event.preventDefault();
      this.close();
    };

    #handleGiftRefresh = (event) => {
      event.preventDefault();
      // Deliberately not followed by cart:updated — that would send the
      // controller round again for a change it made itself.
      this.#refresh();
    };

    #handleExternalUpdate = (event) => {
      if (event.detail?.source === "drawer") return;

      this.#refresh();
    };

    /**
     * Tell the gift controller the cart moved. It cannot observe /cart/add.js
     * and /cart/change.js from here, so a threshold crossed by a quantity
     * change would otherwise never add or remove a reward line.
     *
     * An add from a product form also carries that form's id as `formId`.
     */
    #announceCartUpdate(detail = {}) {
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          bubbles: true,
          detail: { source: "drawer", ...detail },
        }),
      );
    }

    #handleDocumentSubmit = async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!this.isConnected) return;

      const inDrawer = Boolean(form.closest("cart-drawer"));
      const productFormHost = form.closest("product-form");
      // getAttribute, not form.id: the product form holds an input named
      // "id", which shadows the property, so form.id was that input and this
      // never matched — the sticky bar's button fell through to a full-page
      // POST to /cart instead of opening the drawer.
      const formId = form.getAttribute("id");
      const isProductAdd =
        Boolean(event.submitter?.hasAttribute("data-add-to-cart")) ||
        (productFormHost instanceof HTMLElement &&
          Boolean(formId) &&
          formId === productFormHost.dataset.productFormId);

      if (form.closest("cart-page") && !inDrawer) return;
      if (!inDrawer && !isProductAdd) return;
      if (!form.querySelector('[name="id"]')) return;

      event.preventDefault();
      await this.#submitCartForm(form, event.submitter);
    };

    #bindInteractions() {
      const root = this.querySelector("[data-cart-drawer-content]") || this;

      for (const button of root.querySelectorAll(
        "[data-cart-quantity-minus]",
      )) {
        button.onclick = () => this.#changeQuantity(button, -1);
      }

      for (const button of root.querySelectorAll("[data-cart-quantity-plus]")) {
        button.onclick = () => this.#changeQuantity(button, 1);
      }

      for (const button of root.querySelectorAll("[data-cart-remove]")) {
        button.onclick = () => this.#removeLine(button);
      }
    }

    async #changeQuantity(button, delta) {
      await CartLineItems.changeQuantity(
        button,
        delta,
        (lineItem, quantity) => this.#updateLine(lineItem, quantity),
        "drawer",
      );
    }

    async #removeLine(button) {
      await CartLineItems.removeLine(
        button,
        (lineItem, quantity) => this.#updateLine(lineItem, quantity),
        "drawer",
      );
    }

    #getConfirmedLineQuantity(cart, lineKey, requestedQuantity) {
      const item = cart?.items?.find((line) => line.key === lineKey);

      if (item) return item.quantity;
      if (requestedQuantity === 0) return 0;

      return requestedQuantity;
    }

    async #updateLine(lineItem, quantity) {
      const lineKey = lineItem.dataset.lineKey;
      if (!lineKey) {
        throw new Error(this.dataset.errorUpdate ?? "");
      }

      lineItem.setAttribute("aria-busy", "true");

      try {
        const response = await CartLineItems.requestLineQuantityChange(
          lineKey,
          quantity,
        );

        if (!response.ok) {
          const message = await this.#getCartErrorMessage(
            response,
            this.dataset.errorUpdate,
          );
          throw new Error(message);
        }

        const cart = await response.json();
        this.#syncItemCount(cart.item_count);
        this.#clearError();

        if (!(await this.#refresh())) {
          this.#showError(this.dataset.errorRefresh ?? "");
        }

        this.#announceCartUpdate();

        return this.#getConfirmedLineQuantity(cart, lineKey, quantity);
      } catch (error) {
        const message =
          error instanceof Error && error.message
            ? error.message
            : this.dataset.errorUpdate;
        this.#showError(message);
        throw error;
      } finally {
        const activeLineItem =
          this.querySelector(
            `[data-cart-line-item][data-line-key="${CSS.escape(lineKey)}"]`,
          ) ?? lineItem;

        if (activeLineItem.isConnected) {
          activeLineItem.removeAttribute("aria-busy");
        }
      }
    }

    #buildFormData(form) {
      const formData = new FormData(form);
      const formId = form.getAttribute("id");

      if (formId && !formData.has("quantity")) {
        const quantityInput = document.querySelector(
          `[form="${formId}"][name="quantity"]`,
        );

        if (quantityInput instanceof HTMLInputElement) {
          formData.set("quantity", quantityInput.value || "1");
        }
      }

      if (!formData.has("quantity")) {
        formData.set("quantity", "1");
      }

      // Line item properties are bound to the form by id rather than nested
      // inside it, same as the quantity input above.
      if (formId) {
        for (const input of document.querySelectorAll(
          `[form="${formId}"][name^="properties["]`,
        )) {
          if (!(input instanceof HTMLInputElement)) continue;
          if (input.disabled || formData.has(input.name)) continue;

          // An unticked box or an unchosen radio is not part of a submission.
          // Sending the gift card recipient box unticked would ask Shopify to
          // validate a recipient nobody entered, and the add would fail.
          if (
            (input.type === "checkbox" || input.type === "radio") &&
            !input.checked
          ) {
            continue;
          }

          formData.set(input.name, input.value);
        }
      }

      return formData;
    }

    async #submitCartForm(form, submitter) {
      const submitButton =
        submitter instanceof HTMLButtonElement
          ? submitter
          : form.querySelector('[type="submit"]');

      /*
       * The drawer stays shut until the line is actually in the bag. Opening
       * first meant the shopper watched an empty bag for the length of the
       * round trip, and on a fast tap the drawer appeared to show nothing at
       * all (PO review 7 Sep). The wait is acknowledged on the button they
       * pressed instead — data-cart-drawer-adding, styled in this section's
       * stylesheet, which loads on every page the drawer is in the header of.
       */
      submitButton?.setAttribute("disabled", "disabled");
      submitButton?.setAttribute("aria-busy", "true");
      submitButton?.setAttribute("data-cart-drawer-adding", "");

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          body: this.#buildFormData(form),
        });

        if (!response.ok) {
          const message = await this.#getCartErrorMessage(
            response,
            this.dataset.errorAdd,
          );
          throw new Error(message);
        }

        await response.json();
        this.#clearError();

        if (!(await this.#refresh())) {
          const cart = await this.#fetchCart();

          if (cart) {
            this.#syncItemCount(cart.item_count);
          }

          this.#showError(this.dataset.errorRefresh ?? "");
        }

        // Names the form the line came from, so that form can start afresh
        // (the gift card recipient form clears itself on its own add).
        this.#announceCartUpdate({ formId: form.getAttribute("id") });
        this.open();
      } catch (error) {
        const message =
          error instanceof Error && error.message
            ? error.message
            : this.dataset.errorAdd;
        this.#showError(message);
        this.open();
      } finally {
        submitButton?.removeAttribute("disabled");
        submitButton?.removeAttribute("aria-busy");
        submitButton?.removeAttribute("data-cart-drawer-adding");
      }
    }

    async #getCartErrorMessage(response, fallback) {
      try {
        const data = await response.json();
        /*
         * Gift card recipient errors carry `description` as an object of
         * per-field fragments ({ email: ["is invalid"] }); calling .trim() on
         * it threw, and the shopper got the generic message. Shopify's own
         * sentence for them is in `message` ("Validation failed: Email is
         * invalid"), which is read next.
         */
        const description =
          typeof data?.description === "string" ? data.description.trim() : "";

        if (description) return description;

        const message =
          typeof data?.message === "string" ? data.message.trim() : "";

        if (message && message !== "Cart Error") return message;
      } catch {
        // Response body is not JSON.
      }

      return fallback ?? "";
    }

    #showError(message) {
      const errorNode = this.querySelector("[data-cart-drawer-error]");
      if (!errorNode || !message) return;

      errorNode.textContent = message;
      errorNode.hidden = false;
    }

    #clearError() {
      const errorNode = this.querySelector("[data-cart-drawer-error]");
      if (!errorNode) return;

      errorNode.textContent = "";
      errorNode.hidden = true;
    }

    async #fetchCart() {
      try {
        const response = await fetch("/cart.js", {
          headers: { Accept: "application/json" },
        });

        if (!response.ok) return null;

        return response.json();
      } catch {
        return null;
      }
    }

    #syncItemCount(itemCount) {
      const count = Number(itemCount);

      if (!Number.isFinite(count) || count < 0) return;

      this.dataset.itemCount = String(count);
      this.#updateHeaderCart(count);

      const drawerCount = this.querySelector("[data-cart-drawer-count]");

      // The design keeps the count at zero — "My Bag (0)" on the empty frame.
      if (drawerCount) {
        drawerCount.textContent = `(${count})`;
      }
    }

    /**
     * The rewards bar arrives from the Section Rendering API already at its
     * final widths, so the CSS transition on `.cart-thresholds__fill` had no
     * change to run on. Remember where each segment was, start the incoming
     * ones there, and move them to their real values a frame later — once the
     * dialog is open, since a closed dialog has no layout to transition from.
     */
    #readThresholdFills(root) {
      return [...root.querySelectorAll(".cart-thresholds__fill")].map((fill) =>
        fill.style.getPropertyValue("--cart-threshold-fill").trim(),
      );
    }

    #stageThresholds(root, previousFills) {
      this.#pendingThresholds = [];

      [...root.querySelectorAll(".cart-thresholds__fill")].forEach(
        (fill, index) => {
          const target = fill.style.getPropertyValue("--cart-threshold-fill");
          const start = previousFills[index] || "0%";

          if (!target || start === target.trim()) return;

          fill.style.setProperty("--cart-threshold-fill", start);
          this.#pendingThresholds.push({ fill, target });
        },
      );
    }

    #playThresholdsIfOpen() {
      if (this.#dialog?.open) {
        this.#playThresholds();
      }
    }

    #playThresholds() {
      const pending = this.#pendingThresholds;
      if (pending.length === 0) return;

      this.#pendingThresholds = [];

      // After a paint, so the staged start widths have reached layout and the
      // change is something the transition can run from.
      this.#afterPaint(() => {
        for (const { fill, target } of pending) {
          if (fill.isConnected) {
            fill.style.setProperty("--cart-threshold-fill", target);
          }
        }
      });
    }

    #adoptLiveUpsell(currentContent, incomingContent) {
      customElements
        .get("cart-upsell")
        ?.adoptLive?.(currentContent, incomingContent);
    }

    async #refreshSectionContent() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) return false;

      const url = new URL(window.location.href);
      url.searchParams.set("sections", sectionId);

      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const response = await fetch(url.toString());
          if (!response.ok) continue;

          const data = await response.json();
          if (this.#applySectionHtml(data[sectionId])) return true;
        } catch {
          // Retry on network or parse failure.
        }
      }

      return false;
    }

    /**
     * Swaps in a rendered copy of this section. Split from the fetch so a
     * copy that arrived some other way — Shopify's bundled section rendering
     * on a /cart/add.js response — goes in without a second round trip.
     * Returns false when the markup is not a usable drawer, so the caller can
     * fall back to fetching.
     */
    #applySectionHtml(sectionHtml) {
      if (!sectionHtml) return false;

      const template = document.createElement("template");
      template.innerHTML = sectionHtml;

      const incoming = template.content.querySelector(DRAWER_NAME);
      const incomingContent = incoming?.querySelector(
        "[data-cart-drawer-content]",
      );
      const currentContent = this.querySelector("[data-cart-drawer-content]");

      if (!incomingContent || !currentContent) return false;

      // Staged while the fragment is still off-document: reconnecting the
      // carousel below forces a layout, and a fill laid out at its final
      // width first would transition backwards before it moved forward.
      this.#stageThresholds(
        incomingContent,
        this.#readThresholdFills(currentContent),
      );
      /*
       * The scroll container is inside the replaced content, so swapping
       * it lands the shopper back at the top — which is what made the
       * quantity buttons "jump you to the top of the mini cart" (PO
       * review 7 Sep). Carried across by hand; the incoming body is the
       * same height, so the offset still means the same thing.
       */
      const previousBody = currentContent.querySelector(
        "[data-cart-drawer-body]",
      );
      const scrollTop = previousBody?.scrollTop ?? 0;

      this.#adoptLiveUpsell(currentContent, incomingContent);
      currentContent.replaceWith(incomingContent);

      if (scrollTop > 0) {
        const body = incomingContent.querySelector("[data-cart-drawer-body]");
        if (body) body.scrollTop = scrollTop;
      }
      this.#syncItemCount(incoming.dataset.itemCount);
      this.#bindInteractions();
      this.#playThresholdsIfOpen();
      return true;
    }

    /**
     * Every content swap goes through one queue, whichever route the markup
     * took, so an older fetch landing late cannot overwrite a newer copy.
     */
    async #refresh(task = () => this.#refreshSectionContent()) {
      const refreshTask = async () => task();

      const nextRefresh = this.#refreshTail.catch(() => {}).then(refreshTask);
      this.#refreshTail = nextRefresh;
      return nextRefresh;
    }

    #updateHeaderCart(itemCount) {
      const count = Number(itemCount || 0);

      for (const trigger of document.querySelectorAll(
        "[data-cart-drawer-open]",
      )) {
        const badge = trigger.querySelector("[data-cart-count-badge]");
        const label = trigger.querySelector("[data-cart-trigger-label]");

        if (badge) {
          badge.hidden = count === 0;
          badge.textContent = String(count);
        }

        if (label) {
          const cartLabel = trigger.dataset.cartLabel;
          if (!cartLabel) continue;

          label.textContent = count > 0 ? `${cartLabel} (${count})` : cartLabel;
        }
      }
    }
  }

  if (!customElements.get(DRAWER_NAME)) {
    customElements.define(DRAWER_NAME, CartDrawer);
  }
})();
